import { cn } from "~/ui-rtk/utils/tailwind-utils"

export interface SheetTab {
  key: string
  label: string
  /** Small count badge (lines in range) shown after the label. */
  count?: number
  /** Red dot — the company is short of its weekly goal. */
  alert?: boolean
}

/**
 * Spreadsheet-style tab strip above the sheet: the first tab is the
 * Aggregate view, one more per company — like the tabs of the manual
 * launch-tracker workbook. Scrolls horizontally when there are many clients.
 */
export default function SheetTabs({
  tabs,
  activeKey,
  onChange,
}: {
  tabs: SheetTab[]
  activeKey: string
  onChange: (key: string) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Report sheets"
      className="flex items-end gap-0.5 overflow-x-auto rounded-t-2xl border border-b-0 border-white/10 bg-system-blue-900 px-2 pt-2"
    >
      {tabs.map(tab => {
        const isActive = tab.key === activeKey
        return (
          <button
            key={tab.key}
            type="button"
            role="tab"
            aria-selected={isActive}
            onClick={() => onChange(tab.key)}
            className={cn(
              "flex shrink-0 items-center gap-1.5 rounded-t-md border border-b-0 px-3 py-1 text-[12px] whitespace-nowrap transition-colors",
              isActive
                ? "-mb-px border-white/10 bg-system-blue-800/40 font-semibold text-white"
                : "border-transparent bg-white/5 text-system-gray-400 hover:bg-white/10 hover:text-system-gray-200",
            )}
          >
            {tab.alert && (
              <span
                className="h-1.5 w-1.5 rounded-full bg-rose-500"
                aria-label="Short of goal"
              />
            )}
            {tab.label}
            {tab.count !== undefined && (
              <span
                className={cn(
                  "rounded px-1 text-[10px] tabular-nums",
                  isActive
                    ? "bg-system-blue-600/60 text-white"
                    : "bg-white/10 text-system-gray-400",
                )}
              >
                {tab.count}
              </span>
            )}
          </button>
        )
      })}
    </div>
  )
}
